import { Box, MenuItem, TextField } from "@mui/material";
import { ChangeEvent, useEffect, useMemo } from "react";
import {
  FieldValues,
  Path,
  PathValue,
  useController,
  useFormContext,
} from "react-hook-form";

import { BaseFieldProps, DependsOn, Options } from "./FieldProps";
import useIsFieldDisabled from "../../hooks/formHooks/useIsFieldDisabled";
import ArrowDownIcon from "../icons/arrow/ArrowDownIcon";

type Props<TFieldValues extends FieldValues> = {
  options: Options;
  disabled?: boolean;
  dependsOn?: DependsOn<TFieldValues>;
  isRequired?: boolean;
  transform?: (value: string) => boolean;
  parse?: (value: boolean) => string;
  onCustomChange?: (val: string) => void;
} & BaseFieldProps<TFieldValues>;

const SelectField = <TFieldValues extends FieldValues>({
  name,
  label,
  options,
  disabled = false,
  disabledForEdit = false,
  dependsOn,
  isRequired,
  transform,
  parse,
  onCustomChange
}: Props<TFieldValues>) => {
  const { control, clearErrors, watch, setError, trigger } =
    useFormContext<TFieldValues>();
  const { field, fieldState } = useController({
    name,
    control,
  });

  const MenuItems = useMemo(() => {
    return options.map(option => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ));
  }, [options]);

  const formContext = useFormContext();
  const valueToWatch = formContext.watch(dependsOn?.field ?? "");
  const shouldValidate = !!dependsOn;
  const shouldEnable = !shouldValidate || valueToWatch === dependsOn.value;
  const fieldIsDisabled = useIsFieldDisabled(disabled, disabledForEdit);
  const errorMessage = fieldState.error?.message;

  useEffect(() => {
    if (shouldEnable && shouldValidate) {
      if (!field.value || field.value === "") {
        formContext.setError(field.name, {
          message: "Please select at least one option",
          type: "required",
        });
      } else {
        formContext.clearErrors(field.name);
      }
    } else {
      formContext.resetField(field.name);
    }
  }, [shouldEnable, shouldValidate, field.name]);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    if (transform) {
      field.onChange(
        transform(event.target.value) as PathValue<
          TFieldValues,
          Path<TFieldValues>
        >,
      );
    } else {
      field.onChange(event);
    }
    clearErrors(name);
  };

  return (
    <TextField
      {...field}
      onChange={(e) => {
        const val = e.target.value;
        if(onCustomChange){
          onCustomChange(val)
        }
        handleChange(e);
      }}
      value={parse ? parse(field.value) : field.value ?? ""}
      data-testid={`form-${label}-SELECT`}
      select
      label={label}
      disabled={!shouldEnable || fieldIsDisabled}
      error={!!errorMessage}
      helperText={errorMessage}
      required={isRequired}
      SelectProps={{
        IconComponent: props => (
          <Box {...props}>
            <ArrowDownIcon />
          </Box>
        ),
      }}
    >
      {MenuItems}
    </TextField>
  );
};

export default SelectField;
